import React from 'react'
import Link from 'next/link'
import useEmblaCarousel from 'embla-carousel-react'
import Autoplay from 'embla-carousel-autoplay'

import { Icon } from '@wartek-id/icon'

import { ImageFallback } from 'components/ImageFallback'
import { useMediaQueryHelper } from 'app/RuangGTKHomepageV3/hooks/useMediaQueryHelper'
import { getAnchorRelationship, getAnchorTarget } from 'utils/link'
import { setURLQuery } from 'utils/url'
import { useSatuEventTracker } from 'utils/satu-event-tracker'
import {
  SATU_EVENT_DEFAULT_EVENT_TYPE,
  SATU_EVENT_TRACKER_EVENT_NAME,
} from 'utils/satu-event-tracker/config/event-tracking'
import AndroidHandler from 'utils/androidHandler'

type CarouselBanner = {
  id: string
  imageUrl: string
  link?: string
  alt?: string
}

type CarouselProps = {
  banners?: CarouselBanner[]
}

type BaseTrackPayload = {
  topic_id: string
  page_element: number
  num_components: number
  event_trigger?: 'HOMEPAGE' | 'AUTO'
}

type TrackPayload =
  | (BaseTrackPayload & { type: 'click' | 'load' })
  | (BaseTrackPayload & { type: 'swipe'; direction: 'left' | 'right' })

const AUTOPLAY_DELAY = 3000
const FALLBACK_IMAGE = '/images/layout/info-placeholder-illustration.svg'

const trackCarouselEvent = (
  payload: TrackPayload,
  { SETrackEvent, SETransformAttrs }: ReturnType<typeof useSatuEventTracker>
) => {
  if (payload.event_trigger === 'AUTO') return
  const androidHandler = AndroidHandler()
  let activityType: string
  const attributes = {}
  switch (payload.type) {
    case 'click':
      activityType = 'CLICK_HOMEPAGE_CAROUSEL_BANNER'
      attributes['topic_id'] = payload.topic_id
      attributes['page_element'] = payload.page_element
      break
    case 'load':
      activityType = 'LOAD_HOMEPAGE_CAROUSEL_BANNER'
      break
    case 'swipe':
      activityType = `SWIPE_${payload.direction.toUpperCase()}_HOMEPAGE_CAROUSEL_BANNER`
      attributes['topic_id'] = payload.topic_id
      attributes['page_element'] = payload.page_element
      break
    default:
      break
  }

  if (!activityType) return

  SETrackEvent({
    keepAlive: payload.type === 'click',
    eventName: SATU_EVENT_TRACKER_EVENT_NAME,
    eventParams: {
      activity_type: activityType,
      event_name: SATU_EVENT_DEFAULT_EVENT_TYPE,
      attributes: SETransformAttrs({
        ...attributes,
        is_webview: [androidHandler.enabled, 'bool'],
        screen_name: 'HOMEPAGE',
        event_trigger: payload.event_trigger,
        num_components: [payload.num_components, 'int'],
      }),
    },
  })
}

const getDirection = (
  previousIndex: number,
  nextIndex: number,
  total: number
): 'LEFT' | 'RIGHT' => {
  if (previousIndex === total - 1 && nextIndex === 0) return 'RIGHT'
  if (previousIndex === 0 && nextIndex === total - 1) return 'LEFT'
  return nextIndex > previousIndex ? 'RIGHT' : 'LEFT'
}

const BannerLink = ({
  link,
  onClick,
  children,
}: {
  link?: string
  onClick?: React.MouseEventHandler
  children: React.ReactNode
}) => {
  if (!link) {
    return (
      <div role="button" tabIndex={0} onClick={onClick}>
        {children}
      </div>
    )
  }

  const finalLink = setURLQuery(link, {
    utm_source: 'rgtk',
    utm_medium: 'banner',
  })

  const target = getAnchorTarget(finalLink)
  const rel = getAnchorRelationship(finalLink)
  const isInternal = finalLink.startsWith('/')

  if (isInternal) {
    return (
      <Link href={finalLink}>
        <a onClick={onClick}>{children}</a>
      </Link>
    )
  }

  return (
    <a href={finalLink} target={target} rel={rel} onClick={onClick}>
      {children}
    </a>
  )
}

const BannerImage = ({
  src,
  alt,
  priority,
}: {
  src: string
  alt: string
  priority?: boolean
}) => {
  return (
    <ImageFallback
      src={src}
      fallbackSrc={FALLBACK_IMAGE}
      alt={alt}
      layout="fill"
      objectFit="cover"
      objectPosition="center"
      sizes="(min-width: 1024px) 100vw, 100vw"
      priority={priority}
    />
  )
}

const StaticBanner = ({
  banner,
  isDesktop,
  onClick,
}: {
  banner: CarouselBanner
  isDesktop: boolean
  onClick: React.MouseEventHandler
}) => {
  const alt = banner.alt ?? 'Banner'
  return (
    <div className="w-full p-2 flex justify-center">
      <div
        className={`rounded-lg shadow-md aspect-video ${
          isDesktop ? 'w-1/2' : 'w-full'
        }`}
      >
        <div className="relative h-full w-full overflow-hidden rounded-lg">
          <BannerLink link={banner.link} onClick={onClick}>
            <BannerImage src={banner.imageUrl} alt={alt} priority />
          </BannerLink>
        </div>
      </div>
    </div>
  )
}

const Carousel = ({ banners = [] }: CarouselProps) => {
  const satuEventTracker = useSatuEventTracker()
  const { isDesktop } = useMediaQueryHelper()
  const isSingle = banners.length === 1
  const hasMultiple = banners.length > 1

  const autoplayRef = React.useRef(
    Autoplay({
      delay: AUTOPLAY_DELAY,
      stopOnInteraction: true,
      stopOnMouseEnter: true,
    })
  )

  const enableAutoplay = hasMultiple

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: hasMultiple,
      watchDrag: hasMultiple,
      align: 'start',
    },
    enableAutoplay ? [autoplayRef.current] : []
  )

  const [selectedIndex, setSelectedIndex] = React.useState(0)
  const [scrollSnaps, setScrollSnaps] = React.useState<number[]>([])
  const previousIndexRef = React.useRef(0)
  const interactionRef = React.useRef<'HOMEPAGE' | 'AUTO'>('AUTO')
  const loadTrackedRef = React.useRef(false)

  const resetAutoplay = React.useCallback(() => {
    if (!enableAutoplay) return
    autoplayRef.current.reset()
  }, [enableAutoplay])

  const handleBannerClick = React.useCallback(
    (event: React.MouseEvent, banner: CarouselBanner, index: number) => {
      trackCarouselEvent(
        {
          type: 'click',
          topic_id: banner.id,
          page_element: index + 1,
          event_trigger: 'HOMEPAGE',
          num_components: banners.length,
        },
        satuEventTracker
      )
    },
    [banners.length, satuEventTracker]
  )

  const handleDotClick = React.useCallback(
    (index: number) => {
      if (!emblaApi) return
      interactionRef.current = 'HOMEPAGE'
      emblaApi.scrollTo(index)
      resetAutoplay()
    },
    [emblaApi, resetAutoplay]
  )

  const handlePrevClick = React.useCallback(() => {
    if (!emblaApi) return
    interactionRef.current = 'HOMEPAGE'
    emblaApi.scrollPrev()
    resetAutoplay()
  }, [emblaApi, resetAutoplay])

  const handleNextClick = React.useCallback(() => {
    if (!emblaApi) return
    interactionRef.current = 'HOMEPAGE'
    emblaApi.scrollNext()
    resetAutoplay()
  }, [emblaApi, resetAutoplay])

  React.useEffect(() => {
    if (!emblaApi) return

    const updateSnaps = () => {
      setScrollSnaps(emblaApi.scrollSnapList())
      setSelectedIndex(emblaApi.selectedScrollSnap())
    }

    updateSnaps()

    if (!loadTrackedRef.current) {
      loadTrackedRef.current = true
      trackCarouselEvent(
        {
          type: 'load',
          topic_id: banners[0]?.id ?? 'unknown',
          page_element: 1,
          event_trigger: 'HOMEPAGE',
          num_components: banners.length,
        },
        satuEventTracker
      )
    }

    emblaApi.on('reInit', updateSnaps)

    emblaApi.on('select', () => {
      const nextIndex = emblaApi.selectedScrollSnap()
      const previousIndex = previousIndexRef.current

      setSelectedIndex(nextIndex)

      if (previousIndex !== nextIndex) {
        const direction = getDirection(previousIndex, nextIndex, banners.length)
        trackCarouselEvent(
          {
            type: 'swipe',
            direction: direction.toLowerCase() as 'left' | 'right',
            topic_id: banners[nextIndex]?.id ?? 'unknown',
            page_element: nextIndex + 1,
            event_trigger: interactionRef.current,
            num_components: banners.length,
          },
          satuEventTracker
        )
      }

      previousIndexRef.current = nextIndex
      interactionRef.current = 'AUTO'
    })

    emblaApi.on('pointerDown', () => {
      interactionRef.current = 'HOMEPAGE'
    })
  }, [emblaApi, banners, satuEventTracker])

  if (!banners.length) return null

  if (isSingle) {
    return (
      <div
        role="region"
        aria-roledescription="carousel"
        aria-label="Carousel"
        className="w-full"
      >
        <StaticBanner
          banner={banners[0]}
          isDesktop={isDesktop}
          onClick={(event) => handleBannerClick(event, banners[0], 0)}
        />
        <p className="sr-only" aria-live="polite">
          Slide 1 of 1
        </p>
      </div>
    )
  }

  return (
    <div
      role="region"
      aria-roledescription="carousel"
      aria-label="Carousel"
      className="w-full"
    >
      <p className="sr-only" aria-live="polite">
        Slide {selectedIndex + 1} of {banners.length}
      </p>

      {!isDesktop ? (
        <div className="w-full">
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex">
              {banners.map((banner, index) => {
                const alt = banner.alt ?? 'Banner'
                return (
                  <div
                    key={banner.id}
                    className="flex-[0_0_100%] py-2 pr-2 pl-4 first:pl-2"
                  >
                    <div className="w-full rounded-lg shadow-md aspect-video">
                      <div className="relative h-full w-full overflow-hidden rounded-lg">
                        <BannerLink
                          link={banner.link}
                          onClick={(event) =>
                            handleBannerClick(event, banner, index)
                          }
                        >
                          <BannerImage
                            src={banner.imageUrl}
                            alt={alt}
                            priority={index === 0}
                          />
                        </BannerLink>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          <div className="mt-3 flex items-center justify-center gap-2">
            {scrollSnaps.map((_, index) => {
              const isActive = index === selectedIndex
              return (
                <button
                  key={`dot-${index}`}
                  type="button"
                  aria-label={`Go to slide ${index + 1}`}
                  onClick={() => handleDotClick(index)}
                  className={`h-2 w-2 rounded-full border transition-colors ${
                    isActive
                      ? 'border-[#0B5FEF] bg-[#0B5FEF]'
                      : 'border-[#808489] bg-white'
                  }`}
                />
              )
            })}
          </div>
        </div>
      ) : (
        <div className="relative w-full">
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex">
              {banners.map((banner, index) => {
                const alt = banner.alt ?? 'Banner'
                return (
                  <div key={banner.id} className="flex-[0_0_50%] p-2">
                    <div className="w-full rounded-lg shadow-md aspect-video">
                      <div className="relative h-full w-full overflow-hidden rounded-lg">
                        <BannerLink
                          link={banner.link}
                          onClick={(event) =>
                            handleBannerClick(event, banner, index)
                          }
                        >
                          <BannerImage
                            src={banner.imageUrl}
                            alt={alt}
                            priority={index < 2}
                          />
                        </BannerLink>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          <button
            type="button"
            aria-label="Previous slide"
            onClick={handlePrevClick}
            className="absolute -translate-x-1/3 top-1/2 -translate-y-1/2 rounded-full bg-white shadow-md p-2 transition-shadow hover:shadow-lg active:shadow-inner disabled:cursor-not-allowed disabled:opacity-40"
            style={{
              height: '54px',
              width: '54px',
            }}
            disabled={!hasMultiple}
          >
            <Icon className="!text-2xl">chevron_left</Icon>
          </button>

          <button
            type="button"
            aria-label="Next slide"
            onClick={handleNextClick}
            className="absolute translate-x-1/3 right-0 top-1/2 -translate-y-1/2 rounded-full bg-white shadow-md p-2 transition-shadow hover:shadow-lg active:shadow-inner disabled:cursor-not-allowed disabled:opacity-40"
            style={{
              height: '54px',
              width: '54px',
            }}
            disabled={!hasMultiple}
          >
            <Icon className="!text-2xl">chevron_right</Icon>
          </button>

          <div className="mt-3 flex items-center justify-center gap-2">
            {scrollSnaps.map((_, index) => {
              const isActive = index === selectedIndex
              return (
                <button
                  key={`dot-${index}`}
                  type="button"
                  aria-label={`Go to slide ${index + 1}`}
                  onClick={() => handleDotClick(index)}
                  className={`h-2 w-2 rounded-full border transition-colors ${
                    isActive
                      ? 'border-[#0B5FEF] bg-[#0B5FEF]'
                      : 'border-[#808489] bg-white'
                  }`}
                />
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default Carousel
